import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import * as transformers from '~/behaviors/transformers';
import * as schemas from '../schemas';
import { SelectEmployeeAttributes } from '~/interfaces/employee.interface';
import {
  ContinuousSurveyTrendsAnalysisDocument,
  ContinuousSurveyTrendsAnalysisQueryResult,
  FetchTrendResultsDocument,
  FetchTrendResultsQueryResult,
} from '~/gql/graphql';

const fetchTrendResults = createAsyncThunk(
  'survey/fetchTrendResults',
  async (
    {
      companyId,
      languageId,
      surveyIds,
      group,
      isHierarchyReport,
      attributes,
      managerEmployeeIdAttrId,
    }: {
      companyId: string;
      languageId: string;
      surveyIds: string[];
      group: string;
      isHierarchyReport: boolean;
      attributes: Record<string, string[]>;
      managerEmployeeIdAttrId: string;
    },
    { rejectWithValue },
  ) => {
    const { selectedAttributes, selectedManagers } = transformers.attributeFilterFrom(
      attributes,
      // @ts-ignore @TODO Epic WT-21600
      managerEmployeeIdAttrId,
    );

    const res = await graphqlRequest<FetchTrendResultsQueryResult>(FetchTrendResultsDocument, {
      companyIds: [companyId],
      languageId,
      surveyIds,
      aggregationType: group.toUpperCase(),
      isHierarchyReport,
      managerReportIds: selectedManagers,
      attributes: selectedAttributes,
    });
    if (res.error) return rejectWithValue(res.error);

    const ratingMeasurements = transformers.trendsRatingMeasurementsFrom(res.data?.trendsResult, group);

    const normalizedData = normalize(ratingMeasurements, [schemas.trendRatingMeasurement]);

    return normalizedData;
  },
);

const fetchContinuousSurveyTrendsAnalysis = createAsyncThunk(
  'trends/continuousSurveyTrendsAnalysis',
  async (
    {
      surveyId,
      selectedTimeframes,
      attributes,
      languageId,
      managerEmployeeIdAttrId,
      isHierarchyReport,
    }: {
      surveyId: number;
      selectedTimeframes: { startTimeUTC: Date; endTimeUTC: Date }[];
      attributes: SelectEmployeeAttributes;
      languageId: number;
      managerEmployeeIdAttrId: number;
      isHierarchyReport: boolean;
    },
    { rejectWithValue },
  ) => {
    const { selectedAttributes, selectedManagers } = transformers.attributeFilterFrom(
      attributes,
      managerEmployeeIdAttrId,
    );

    const res = await graphqlRequest<ContinuousSurveyTrendsAnalysisQueryResult>(
      ContinuousSurveyTrendsAnalysisDocument,
      {
        input: {
          surveyId,
          languageId,
          selectedTimeframes,
          attributes: selectedAttributes,
          managerReportIds: selectedManagers,
          isHierarchyReport,
        },
      },
    );
    if (res.error) return rejectWithValue(res.error);

    // Reuse existing group argument as the type of trends
    const ratingMeasurements = transformers.trendsRatingMeasurementsFrom(
      res.data?.continuousSurveyTrendsAnalysis,
      'timeframe',
    );
    const normalizedData = normalize(ratingMeasurements, [schemas.trendRatingMeasurement]);
    return normalizedData;
  },
);

export const trendsActions = {
  fetchTrendResults,
  fetchContinuousSurveyTrendsAnalysis,
};
